/**
 * useQRLogos - Hook for QR logo upload/list/delete operations
 */

import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { authenticatedFetch } from '@/lib/api';
import type { QRLogo } from '@/types/qrCode';

const API_BASE = '/api/v1/admin/qr-logos';

interface UseQRLogosReturn {
  logos: QRLogo[];
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;
  fetchLogos: () => Promise<void>;
  uploadLogo: (file: File, name: string) => Promise<QRLogo | null>;
  deleteLogo: (id: string) => Promise<boolean>;
}

export function useQRLogos(): UseQRLogosReturn {
  const [logos, setLogos] = useState<QRLogo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogos = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(API_BASE);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      setLogos(result.data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load logos';
      setError(message);
      console.error('[useQRLogos] fetchLogos error:', err);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const uploadLogo = useCallback(async (file: File, name: string): Promise<QRLogo | null> => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('name', name);
      const response = await authenticatedFetch(API_BASE, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${response.status}`);
      }
      const result = await response.json();
      toast.success('Logo uploaded');
      await fetchLogos();
      return result.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload logo';
      toast.error(message);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, [fetchLogos]);

  const deleteLogo = useCallback(async (id: string): Promise<boolean> => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      toast.success('Logo deleted');
      await fetchLogos();
      return true;
    } catch {
      toast.error('Failed to delete logo');
      return false;
    }
  }, [fetchLogos]);

  return { logos, isLoading, isUploading, error, fetchLogos, uploadLogo, deleteLogo };
}
